import { FunctionComponent } from "react";

import { Checkbox } from "@/components/Checkbox/Checkbox";
import { InputField } from "@/components/InputField/InputField";
import { SelectField } from "@/components/SelectField/SelectField";

import { TopicCapture, TopicConfig } from "../onboardingListTypes";
import { ConfigField, ConfigGrid, useConfigTranslation, useFieldError } from "./ConfigField";
import { ConfigFormProps } from "./configFormProps";

const TIME_FRAMES = [7, 30, 90];

const CAPTURE_KEYS: { key: keyof TopicCapture; labelKey: string }[] = [
	{ key: "posts", labelKey: "capturePosts" },
	{ key: "likes", labelKey: "captureLikes" },
	{ key: "comments", labelKey: "captureComments" },
];

/** LI_POST / BY_TOPIC — the topic, who posted about it, and which engagement counts. */
export const TopicForm: FunctionComponent<ConfigFormProps<TopicConfig>> = ({
	value,
	errors,
	onChange,
}) => {
	const ts = useConfigTranslation();
	const errorFor = useFieldError(errors);

	const toggleCapture = (key: keyof TopicCapture) =>
		onChange({ ...value, capture: { ...value.capture, [key]: !value.capture[key] } });

	return (
		<ConfigGrid>
			<ConfigField required error={errorFor("topic")} label={ts("topic")}>
				<InputField
					error={Boolean(errors.topic)}
					handleChange={(event) => onChange({ ...value, query: event.target.value })}
					name="topicQuery"
					placeholder={ts("topicPlaceholder")}
					showError={Boolean(errors.topic)}
					value={value.query}
				/>
			</ConfigField>

			<ConfigField label={ts("authorJobTitle")}>
				<InputField
					handleChange={(event) => onChange({ ...value, authorJobTitle: event.target.value })}
					name="authorJobTitle"
					placeholder={ts("authorJobTitlePlaceholder")}
					value={value.authorJobTitle}
				/>
			</ConfigField>

			<ConfigField label={ts("timeFrame")}>
				<SelectField
					hideEmptyValue
					handleSelect={(timeFrame) => onChange({ ...value, timeFrame: Number(timeFrame) })}
					options={TIME_FRAMES.map((days) => ({
						title: ts(`timeFrame${days}`),
						value: days,
					}))}
					selectedOption={value.timeFrame}
					testId="onboarding-topic-timeFrame"
				/>
			</ConfigField>

			<ConfigField error={errorFor("capture")} label={ts("capture")}>
				<div className="flex flex-col gap-2 pt-1">
					{CAPTURE_KEYS.map(({ key, labelKey }) => (
						<Checkbox key={key} isChecked={value.capture[key]} onChange={() => toggleCapture(key)}>
							<span className="text-ssm">{ts(labelKey)}</span>
						</Checkbox>
					))}
				</div>
			</ConfigField>
		</ConfigGrid>
	);
};
